import { Widget } from '../widget/widget.js';
import { WidgetIndex, WidgetValidationError } from '../widget/widgetIndex.js';
import { ApiResponse } from '../api/api.js';
import { ApiClient, ApiClientFactory } from '../api/apiclientfactory.js';
import { InvalidInputError } from '../errors/invalidInputError.js';
import { WidgetComponent } from '../widget/widgetComponent.js';
import { Entity } from '../formats/entity.js';
import { ResourceApi, ResourceUpdatedResponse } from '../api/resourceApi.js';
import { CompleteEntityData } from '../api/adminResourceApi.js';

declare let baseUrl: string;

export type ResourceSavedEvent = {resourceId: number, resolve: () => void};

export class Form {
    private readonly resource: string;

    protected readonly langs: Array<string>;

    protected api: ApiClient;

    private resourceApi: ResourceApi<Entity>;

    private widgets: Record<string, typeof Widget>;

    protected widgetIndex: WidgetIndex;

    /**
     * @param resource - name of the resource
     * @param langs - array of present languages
     * @param apiClient - instance of an ApiClient
     * @param widgets - available widgets for this form
     * @param args - additional args to be merged with this
     */
    constructor(
        resource: string,
        langs: Array<string>,
        apiClient: ApiClient,
        widgets: Record<string, typeof Widget>,
        args?: Record<string, unknown>,
    ) {
        this.resource = resource;
        this.langs = langs;
        this.api = apiClient;
        this.resourceApi = this.api.resource<Entity>(this.resource);
        this.widgets = widgets;
        this.widgetIndex = new WidgetIndex();

        if (args !== null) Object.assign(this, args);

        this.initWidgets();
        this.widgetIndex.observe();
    }

    getDefaultWidgetArgs(): any {
        return {
            apiUrl: baseUrl || null,
            form: this,
        };
    }

    filter(controls: NodeList): HTMLElement[] {
        return Array
            .from(controls)
            .filter(control => (control.parentNode as HTMLElement).classList.contains('is-widget-group-child') === false) as HTMLElement[];
    }

    /**
     * creates the widgets, sets the language and adds them to the widgetIndex
     */
    initWidgets(formNode?: Element): void {
        const widgetsUnfiltered = typeof formNode !== 'undefined'
            ? formNode.querySelectorAll('div[data-widget]')
            : document.querySelectorAll('div[data-widget]')
        const widgets: HTMLElement[] = this.filter(widgetsUnfiltered);

        widgets.forEach(node => {
            const data = node.dataset;
            const args = this.getDefaultWidgetArgs();

            // add language to multilingual widgets
            if (typeof data.widgetLang !== 'undefined' && data.widgetLang !== '') {
                args.lang = data.widgetLang;
            }

            // if widgetname has backslash, assume string is a class with namespace
            // and therefore take last segment of namespace
            const widgetClass: typeof Widget = (data.widget.indexOf('\\') !== -1)
                ? this.widgets[data.widget.substr(data.widget.lastIndexOf('\\') + 1)]
                : this.widgets[data.widget];

            // do not ignore widgetComponents
            if (data.widget === 'component') {
                const widgetInstance = node.querySelector('[data-component]') as unknown as WidgetComponent;
                if (widgetInstance !== null) {
                    this.widgetIndex.addWidget(widgetInstance);
                } else {
                    console.log(`${data.widgetName} (${data.widget}) should have an element containing a [data-component] attribute.`);
                }
                // eslint-disable-next-line brace-style
            }

            // create widget instance
            else if (typeof widgetClass !== 'undefined') {
                const widgetInstance = new (widgetClass as any)(node, args);
                if (typeof widgetInstance === 'undefined' || widgetInstance === null) {
                    console.log(`${data.widgetName} (${data.widget}) could not be instantiated`, widgetClass);
                }
                this.widgetIndex.addWidget(widgetInstance);
            } else {
                console.log(`${data.widgetName} (${data.widget}) has no valid javascript class and thus won’t get added to the WidgetIndex`, widgetClass);
            }
        });
    }

    async validate(): Promise<boolean> {
        return this.widgetIndex.validate();
    }

    async preUploadTasks(): Promise<boolean> {
        return this.widgetIndex.runPreUploadTasks();
    }

    /**
     * gets all non-multilingual widgets
     */
    async getValues(): Promise<Map<string, any>> {
        const allWidgets = this.widgetIndex.widgets;

        // only use widgets that are not multilingual
        const widgets = allWidgets.filter((w: Widget) => w.lang === '');
        return this.widgetIndex.getValues(widgets);
    }

    /**
     * gets all multilingual widgets
     */
    async getMultiLangValues(lang: string): Promise<Map<string, any>> {
        const allWidgets = this.widgetIndex.widgets;

        // only use widgets that are of the correct language
        const widgets = allWidgets.filter((w: Widget) => w.lang === lang);
        return this.widgetIndex.getValues(widgets);
    }

    /**
     * validates widgets and posts the values to the api
     */
    async send(): Promise<InvalidInputError|boolean|ApiResponse> {
        if (await this.validate() === false) {
            return false;
        }

        if (await this.preUploadTasks() === false) {
            return false;
        }

        const values = await this.getValues();
        let modelPromise: Promise<ApiResponse<ResourceUpdatedResponse>>;
        if (values.has('id') && values.get('id') !== 0 && values.get('id') !== null) {
            modelPromise = this.resourceApi.patch(Object.fromEntries(values.entries()));
        } else {
            modelPromise = this.resourceApi.post(Object.fromEntries(values.entries()));
        }

        return modelPromise.then(async (modelApiResponse: ApiResponse<ResourceUpdatedResponse>) => {
            const resourceId = (values.has('id') && values.get('id') !== 0 && values.get('id') !== null)
                ? parseInt(values.get('id'))
                : modelApiResponse.data.id;

            // insert languages
            for (const lang of this.langs) {
                const langData = await this.getMultiLangValues(lang);
                langData.set('id', resourceId);
                await this.resourceApi.patchLanguage(
                    lang,
                    Object.fromEntries(langData.entries()),
                );
            }

            return this.dispatchSaveEvent(resourceId);
        }).catch(this.widgetErrorCatcher.bind(this));
    }

    /**
     * validates widgets and posts the values to the api
     */
    async sendComplete(): Promise<InvalidInputError|boolean|ApiResponse> {
        if (await this.validate() === false) {
            return false;
        }

        if (await this.preUploadTasks() === false) {
            return false;
        }

        const values = await this.getValues();
        const data: CompleteEntityData = {
            general: Object.fromEntries(values.entries()),
            langs: Object.fromEntries(await Promise.all(this.langs.map(async lang => [lang, Object.fromEntries((await this.getMultiLangValues(lang)).entries())]))),
            misc: { revisionComment: (document.querySelector('input[name="revision_comment"]') as HTMLInputElement)?.value ?? null },
        };

        try {
            const apiResponse = await ApiClientFactory().adminResource(this.resourceApi.resource).saveCompleteEntity(data);
            const resourceId = (values.has('id') && parseInt(values.get('id')) !== 0 && values.get('id') !== null)
                ? parseInt(values.get('id'))
                : apiResponse.data.results.id;

            return this.dispatchSaveEvent(resourceId);
        } catch (error) {
            this.widgetErrorCatcher(error);
            return false;
        }
    }

    async dispatchSaveEvent(resourceId: number): Promise<boolean> {
        // trigger onSave event
        const widgets = document.querySelectorAll('[data-needs-resource-id]');
        const createEventPromise = (el: EventTarget, bubbles: boolean): Promise<void> => new Promise(resolve => {
            const event = new CustomEvent<ResourceSavedEvent>('resource-saved', { detail: { resourceId, resolve }, bubbles });
            el.dispatchEvent(event);
        });

        // trigger event on all widgets with data-needs-resource-id
        for (const widget of widgets) {
            await createEventPromise(widget, false);
        }

        // trigger event on document
        createEventPromise(document, true); // no need to await this one

        return true;
    }

    /**
     * annotate widgets with validation error from backend
     */
    widgetErrorCatcher(error: InvalidInputError): InvalidInputError {
        // refresh each widget if implemented
        this.widgetIndex.widgets.map(async w => w.refresh());

        // reject error if no additional errors defined
        if (typeof error.errors === 'undefined') {
            return error;
        }

        for (let i = 0; i < error.errors.length; i++) {
            const e: WidgetValidationError = error.errors[i];
            if (e.valid === true) continue;
            if (e.errors.length > 0) {
                const widget = this.widgetIndex.findWidget(e.widget, e.lang);
                if (widget === false) {
                    console.log('WidgetIndex could not find ', e.widget, e);
                } else {
                    try {
                        widget.displayError(e.errors.join(', '));
                    } catch (ex) {
                        // catch WidgetException
                    }
                }
            }
        }

        return error;
    }

    getWidgetIndex(): WidgetIndex {
        return this.widgetIndex;
    }
}
